import I18n from 'i18n-js';
import React from 'react';

import RecentlyViewedListings from '@reverbdotcom/commons/src/cms/components/recently_viewed_listings';
import AffinityBasedCmsEmbed from '@reverbdotcom/commons/src/components/affinity_based_cms_embed';
import CMSSearchInputGroup from '@reverbdotcom/commons/src/components/cms_search_input_group';
import { OverflowingRowAction } from '@reverbdotcom/commons/src/components/overflowing_row_action';
import WatchedListings from '@reverbdotcom/commons/src/components/watched_listings';
import experiments from '@reverbdotcom/commons/src/experiments';
import { getItem } from '@reverbdotcom/commons/src/local_storage_utils';
import { useExpEnabled, useUser } from '@reverbdotcom/commons/src/user_hooks';
import { useShowCountryFlags } from '@reverbdotcom/commons/src/listing_card_helpers';
import {
  HomepageDealsAndStealsPersonalizedListingsCollection,
} from '../../../../components/homepage_deals_and_steals_personalized_listings_collection';
import {
  PERSONALIZED_HOLIDAY_FULL_BLEED,
  PERSONALIZED_HOLIDAY_FULL_BLEED_FALLBACK,
  PERSONALIZED_HOLIDAY_LOW,
  PERSONALIZED_HOLIDAY_LOW_FALLBACK,
  PERSONALIZED_HOLIDAY_MID,
  PERSONALIZED_HOLIDAY_MID_FALLBACK,
} from '../../../../shared/personalized_cms_embed_mappings';
import CPRecommendationsListingsRow from '../../../recommended_listings/cp_recommendations_listings_row';
import RecommendedListingsRow from '../../../recommended_listings/recommended_listings_row';
import { Paths } from '../../../shared/url_helpers';
import RegionalListingsRow from '../../regional_listings';
import { buildRecentSearches } from '@reverbdotcom/commons/src/components/site_search/reverb_site_search_suggestions';
import HomepageSearchRecommendationsContent from '../homepage_search_recommendations';
import { JustListedForYouRow } from '../just_listed_for_you';
import { PromotedListingsRow as PromotedListingsRowContent } from '../promoted_listings_row';
import RecentArticlesContent from '../recent_articles';

import RQLHomePageFeed from '../../../../feed/rql_homepage_feed';

export { RQLHomePageFeed as FromYourFeed };

import CmsPageCards, { CMS2_PAGE } from '@reverbdotcom/commons/src/cms/components/cms_page_cards';
import HomepageCategories from '../../homepage_categories';
import { affinityBasedPromoEligibleRegionAndLocale, buyingGuideEligibleRegionAndLocale } from '../../user_helper';

import RecommendedCspsRow from '../../../recommended_csps_row';

export { RecommendedCspsRow };

export function HolidayPromotionalBanner() {
  const user = useUser();
  if (!affinityBasedPromoEligibleRegionAndLocale(user.shippingRegionCode, user.locale)) {
    return null;
  }

  return (
    <AffinityBasedCmsEmbed
      slugOptionsByAffinity={PERSONALIZED_HOLIDAY_FULL_BLEED}
      fallback={PERSONALIZED_HOLIDAY_FULL_BLEED_FALLBACK}
      componentName="PersonalizedHomepagePromotionalBanner"
      ssrMode="full"
    />
  );
}

export function HolidayEmbedMid() {
  const user = useUser();
  if (!affinityBasedPromoEligibleRegionAndLocale(user.shippingRegionCode, user.locale)) {
    return null;
  }

  return (
    <AffinityBasedCmsEmbed
      slugOptionsByAffinity={PERSONALIZED_HOLIDAY_MID}
      fallback={PERSONALIZED_HOLIDAY_MID_FALLBACK}
      componentName="PersonalizedHomepageMid"
      ssrMode="full"
    />
  );
}

export function HolidayEmbedLow() {
  const user = useUser();
  if (!affinityBasedPromoEligibleRegionAndLocale(user.shippingRegionCode, user.locale)) {
    return null;
  }

  return (
    <AffinityBasedCmsEmbed
      slugOptionsByAffinity={PERSONALIZED_HOLIDAY_LOW}
      fallback={PERSONALIZED_HOLIDAY_LOW_FALLBACK}
      componentName="PersonalizedHomepageLow"
    />
  );
}

export function HomepageRecentlyViewedListings() {
  const user = useUser();
  const showFlags = useShowCountryFlags(user, experiments.COUNTRY_FLAGS_HOME_PAGE);

  return (
    <RecentlyViewedListings
      title={I18n.t('discovery.homepage.recentlyViewed')}
      layoutSection="main"
      pagePosition={1}
      ssr={true}
      showFlags={showFlags}
    />
  );
}

export function RecommendedForYou() {
  const user = useUser();
  const limit = 6;
  const showFlags = useShowCountryFlags(user, experiments.COUNTRY_FLAGS_HOME_PAGE);

  if (useExpEnabled(experiments.CP_RECOMMENDATIONS)) {
    return null;
  }

  return (
    <RecommendedListingsRow
      limit={limit}
      showFlags={showFlags}
    />
  );
}

export function RecommendedForYouCP() {
  if (useExpEnabled(experiments.CP_RECOMMENDATIONS)) {
    return (
      <CPRecommendationsListingsRow
        title={I18n.t('discovery.recommendedListings.cpRecommendationsTitle')}
        listingsLimit={24}
        recsLimit={40}
      />
    );
  }

  return null;
}

export function RecentArticles() {
  return (
    <RecentArticlesContent
      action={
        <OverflowingRowAction
          href={Paths.articlesSearch.expand({})}
          text={I18n.t('discovery.homepage.buyingGuides.seeMore')}
        />
      }
    />
  );
}

export function PromotedListingsRow() {
  return (
    <PromotedListingsRowContent />
  );
}

export function HomepageSearchRecommendations() {
  const recentSearches = buildRecentSearches(getItem('RECENT_SEARCHES') || [])?.suggestions[0]?.results || [];
  return <HomepageSearchRecommendationsContent recentSearches={recentSearches} />;
}

export function JustListedForYou() {
  return (
    <JustListedForYouRow />
  );
}

export function DealsAndStealsForYou() {
  return <HomepageDealsAndStealsPersonalizedListingsCollection />;
}

export function RegionalListings() {
  return <RegionalListingsRow />;
}

export function FromYourWatchlist() {
  const user = useUser();
  const showFlags = useShowCountryFlags(user, experiments.COUNTRY_FLAGS_HOME_PAGE);

  return (
    <WatchedListings
      mainCallToAction={(
        <OverflowingRowAction
          href={Paths.myFavorites.expand({})}
          text={I18n.t('commons.watchRow.loggedIn.cta')}
        />
      )}
      secondaryCallToAction={(
        <>
          <div className="inner-call-to-action__mobile">
            <CMSSearchInputGroup
              actionUrl={Paths.marketplace.expand({})}
              placeholder={I18n.t('commons.watchRow.loggedIn.emptyState.condensedCta')}
            />
          </div>
          <div className="inner-call-to-action__non-mobile">
            <CMSSearchInputGroup
              actionUrl={Paths.marketplace.expand({})}
              placeholder={I18n.t('commons.watchRow.loggedIn.emptyState.cta')}
            />
          </div>
        </>
      )}
      showFlags={showFlags}
    />
  );
}

export function BuyingGuidesOrCategories() {
  const user = useUser();
  if (buyingGuideEligibleRegionAndLocale(user.shippingRegionCode, user.locale)) {
    return (
      <CmsPageCards
        title={I18n.t('discovery.homepage.buyingGuides.buyingGuidesHeading')}
        pageFields={[
          { pageSource: CMS2_PAGE, slug: 'buying-guide-acoustic-guitars' },
          { pageSource: CMS2_PAGE, slug: 'buying-guide-telecasters' },
          { pageSource: CMS2_PAGE, slug: 'buying-guide-audio-interfaces' },
          { pageSource: CMS2_PAGE, slug: 'buying-guide-gibson-les-pauls' },
          { pageSource: CMS2_PAGE, slug: 'buying-guide-affordable-monosynths' },
          { pageSource: CMS2_PAGE, slug: 'buying-guide-reverb-pedals' },
        ]}
        ssr={false}
        action={
          <OverflowingRowAction
            href={Paths.buyingGuide.expand({})}
            text={I18n.t('discovery.homepage.buyingGuides.seeMore')}
          />
        }
      />
    );
  }

  return <HomepageCategories />;
}
